import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Suchfeld für TN-Suche
function TnSearchDropdown({ rechnungId, onSelect, disabled }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const { data: d } = await axios.get(`${API}/api/admin/tn-search?q=${encodeURIComponent(q)}&limit=20`);
        setResults(d.items || []);
        setShowResults(true);
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  return (
    <div className="relative">
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => setShowResults(true)}
        onBlur={() => setTimeout(() => setShowResults(false), 200)}
        placeholder="TN suchen (min. 2 Zeichen)…"
        className="w-full px-3 py-2 border border-stone-300 rounded text-sm"
        data-testid="tn-search-input"
        disabled={disabled}
      />
      {showResults && results.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-stone-200 rounded shadow-lg max-h-80 overflow-y-auto">
          {results.map(t => (
            <button
              key={t.id}
              onMouseDown={() => onSelect(t)}
              className="w-full text-left px-3 py-2 hover:bg-stone-50 text-sm border-b border-stone-100 last:border-0"
            >
              <div className="font-medium">{t.nachname}, {t.vorname}</div>
              <div className="text-xs text-stone-500">
                {t.kurs || 'kein Kurs'} · Start: {t.kurs_start?.slice(0,10) || '–'} ·
                <span className={t.finanzierungsart === 'bildungsgutschein' ? ' text-emerald-600' : ' text-rose-600'}> {t.finanzierungsart || 'leer'}</span>
                {t.zertifikatsnummer && ` · Zert: ${t.zertifikatsnummer}`}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RechnungenTnZuordnung() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], total_afa: 0, unmatched: 0, with_suggestions: 0 });
  const [expandedFallback, setExpandedFallback] = useState({});
  const [processing, setProcessing] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/api/admin/afa-match-suggestions`);
      setData(d);
    } catch (e) {
      toast.error('Laden fehlgeschlagen: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const linkTn = async (rechnung_id, teilnehmer_id, tn_label) => {
    if (!window.confirm(`TN "${tn_label}" verknüpfen und automatisch auf Bildungsgutschein setzen?`)) return;
    setProcessing(p => ({ ...p, [rechnung_id]: true }));
    try {
      const { data: d } = await axios.post(`${API}/api/admin/link-rechnung-tn`, { rechnung_id, teilnehmer_id, also_mark_bg: true });
      if (d.ok) {
        toast.success(`✓ ${d.rechnung} → ${d.teilnehmer}${d.tn_auf_bg_gesetzt ? ' (auf BG gesetzt)' : ''}`);
        setData(cur => ({
          ...cur,
          items: cur.items.filter(it => it.rechnung_id !== rechnung_id),
          unmatched: cur.unmatched - 1,
        }));
      } else {
        toast.error(d.error || 'Fehler beim Verknüpfen');
      }
    } catch (e) {
      toast.error('Verknüpfung fehlgeschlagen: ' + e.message);
    } finally {
      setProcessing(p => ({ ...p, [rechnung_id]: false }));
    }
  };

  const toggleFallback = (id) => setExpandedFallback(e => ({ ...e, [id]: !e[id] }));

  if (loading) return <div className="p-8 text-center text-stone-500">Lade AfA-Rechnungen …</div>;

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="rechnungen-tn-zuordnung">
      <div className="border-b border-stone-200 pb-4">
        <h1 className="text-3xl font-serif text-stone-800">Rechnungen ↔ Teilnehmer-Zuordnung</h1>
        <p className="text-sm text-stone-500 mt-2">
          AfA-Rechnungen automatisch mit Teilnehmern verknüpfen. Verknüpfung setzt Teilnehmer sofort auf Bildungsgutschein.
        </p>
        <div className="flex gap-6 mt-4 text-sm">
          <div><span className="text-stone-500">AfA gesamt:</span> <b>{data.total_afa}</b></div>
          <div><span className="text-stone-500">Unverknüpft:</span> <b className="text-orange-600">{data.unmatched}</b></div>
          <div><span className="text-stone-500">Mit Vorschlägen:</span> <b className="text-emerald-600">{data.with_suggestions}</b></div>
          <div><span className="text-stone-500">Ohne Vorschläge:</span> <b className="text-rose-600">{data.without_suggestions}</b></div>
        </div>
      </div>

      {data.items.length === 0 ? (
        <div className="p-12 text-center text-stone-400">✓ Alle AfA-Rechnungen sind verknüpft.</div>
      ) : (
        <div className="space-y-4">
          {data.items.map(rech => (
            <div key={rech.rechnung_id} className="border border-stone-200 rounded-lg bg-white shadow-sm p-5" data-testid={`rechnung-card-${rech.rechnungsnummer}`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="font-mono text-sm text-stone-500">{rech.rechnungsnummer} · {rech.rechnungsdatum?.slice(0,10)}</div>
                  <div className="font-semibold text-stone-800">{rech.empfaenger_name}</div>
                  <div className="text-sm text-stone-600 mt-1">{rech.positionen_text}</div>
                  {rech.einleitungstext && (
                    <div className="text-xs text-stone-500 mt-2 italic bg-stone-50 p-2 rounded whitespace-pre-line">
                      {rech.einleitungstext.slice(0, 200)}
                    </div>
                  )}
                </div>
                <div className="text-right text-sm text-stone-600">
                  <div>{rech.endbetrag?.toFixed(2)} €</div>
                  <div className="text-xs">{rech.empfaenger_typ}</div>
                </div>
              </div>

              {rech.kandidaten.length > 0 ? (
                <div className="space-y-2 mt-4">
                  <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Vorgeschlagene Teilnehmer</div>
                  {rech.kandidaten.map((c, i) => (
                    <div key={c.tn_id} className="flex items-center justify-between p-3 bg-stone-50 rounded border border-stone-100" data-testid={`kandidat-${i}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded ${c.score >= 90 ? 'bg-emerald-100 text-emerald-700' : c.score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-600'}`}>
                            Score {c.score}
                          </span>
                          <span className="font-semibold text-stone-800">{c.nachname}, {c.vorname}</span>
                          <span className="text-xs text-stone-500">Start: {c.kurs_start?.slice(0,10)}</span>
                          {c.zertifikatsnummer && <span className="text-xs text-stone-500">Zert: {c.zertifikatsnummer}</span>}
                          <span className={`text-xs px-1.5 py-0.5 rounded ${c.aktuelle_finanzierung === 'bildungsgutschein' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                            {c.aktuelle_finanzierung || 'leer'}
                          </span>
                        </div>
                        <div className="text-xs text-stone-500 mt-1">{c.begruendung}</div>
                      </div>
                      <button
                        onClick={() => linkTn(rech.rechnung_id, c.tn_id, `${c.nachname}, ${c.vorname}`)}
                        disabled={processing[rech.rechnung_id]}
                        className="ml-3 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm rounded font-medium disabled:opacity-50"
                        data-testid={`btn-link-${i}`}
                      >
                        Zuordnen → BG
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 p-3 bg-rose-50 border border-rose-100 rounded text-sm text-rose-700">
                  Keine automatischen Vorschläge (BG-Nr / Kurs / Datum passt nicht)
                </div>
              )}

              {rech.fallback_kandidaten && rech.fallback_kandidaten.length > 0 && (
                <div className="mt-4 border-t border-stone-200 pt-3">
                  <button
                    onClick={() => toggleFallback(rech.rechnung_id)}
                    className="text-sm text-stone-600 hover:text-stone-800 flex items-center gap-2"
                    data-testid="btn-toggle-fallback"
                  >
                    <span>{expandedFallback[rech.rechnung_id] ? '▼' : '▶'}</span>
                    <span>Alle TN in diesem Kurs zeigen ({rech.fallback_kandidaten.length})</span>
                  </button>
                  {expandedFallback[rech.rechnung_id] && (
                    <div className="mt-2 space-y-1 max-h-96 overflow-y-auto">
                      {rech.fallback_kandidaten.map(f => (
                        <div key={f.tn_id} className="flex items-center justify-between p-2 bg-white border border-stone-100 rounded text-sm">
                          <div className="flex items-center gap-3 flex-1">
                            <span className="font-medium">{f.nachname}, {f.vorname}</span>
                            <span className="text-xs text-stone-500">Start: {f.kurs_start?.slice(0,10)}</span>
                            {f.zertifikatsnummer && <span className="text-xs text-stone-500">Zert: {f.zertifikatsnummer}</span>}
                            <span className={`text-xs px-1.5 py-0.5 rounded ${f.aktuelle_finanzierung === 'bildungsgutschein' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                              {f.aktuelle_finanzierung || 'leer'}
                            </span>
                          </div>
                          <button
                            onClick={() => linkTn(rech.rechnung_id, f.tn_id, `${f.nachname}, ${f.vorname}`)}
                            disabled={processing[rech.rechnung_id]}
                            className="ml-2 px-3 py-1 bg-stone-600 hover:bg-stone-700 text-white text-xs rounded"
                          >
                            Zuordnen
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Freie TN-Suche für alle Rechnungen */}
              <div className="mt-4 border-t border-stone-200 pt-3">
                <div className="text-xs text-stone-500 mb-2">Anderen TN manuell suchen:</div>
                <TnSearchDropdown
                  rechnungId={rech.rechnung_id}
                  onSelect={(t) => linkTn(rech.rechnung_id, t.id, `${t.nachname}, ${t.vorname}`)}
                  disabled={processing[rech.rechnung_id]}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
